import { QueryFactory } from '@lmt/shared-types';
import { initializeAxios } from '@lmt/unicorn-shared/app/api/client-factory';

import '@lmt/unicorn-shared/app/components/global-forms/GlobalFormContent.scss';
import { configureReactQueryCache } from '@lmt/unicorn-shared/app/helpers/configure-react-query';
import { NetworkError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { KnownError } from '@lmt/unicorn-shared/app/helpers/KnownError';
import { UniStorageFactory } from '@lmt/unicorn-shared/app/helpers/uni-storage';
import 'devextreme/dist/css/dx.light.css';
import { QueryClient, matchQuery, onlineManager } from '@tanstack/react-query';
import 'react-toastify/dist/ReactToastify.css';
import '../app.global.css';
import '../App.global.scss';
import './helpers/cached-images';
import equal from 'fast-deep-equal';
import { persistQueryClientSave } from '@tanstack/react-query-persist-client';
import { addLogoutHandler } from './features/auth/operations';
import {
  loadSavedTimezoneIfPresent,
  saveTimezone,
} from './helpers/timezone-change-helper';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      structuralSharing: (oldData: any, newData) =>
        equal(oldData, newData) ? oldData : newData,

      refetchOnWindowFocus: false,
      gcTime: Infinity,
      // we need at least a bit of staletime, otherwise queries restored from the cache are run twice
      staleTime: 1000,
      throwOnError: true,
      networkMode: 'always',
      retry: (errorCount, error: any) => {
        if (errorCount >= 3) return false;
        if (error?.message === NetworkError) return false;
        if (error.status === 403) return false;
        if (error.status === 401) return false;
        if (error.status === 404) return false;
        if (error instanceof KnownError) return false;
        return true;
      },
    },
  },
});

// otherwise react-query won't send any requests to local backend
// when no ethernet or wi-fi is connected
onlineManager.setOnline(true);
loadSavedTimezoneIfPresent();
const reactQueryPersister = configureReactQueryCache(queryClient);
addLogoutHandler(() => {
  saveTimezone(null);
  queryClient.removeQueries({
    predicate(query) {
      return !matchQuery(
        { queryKey: QueryFactory.UserQuery.getCurrentUserInfoQueryKey() },
        query,
      );
    },
  });
  // this is to make `useIsAuthorized` hook rerender, return false, and log the user out
  queryClient.setQueryData(
    QueryFactory.UserQuery.getCurrentUserInfoQueryKey(),
    null,
  );
});

//this is mostly for UI tests to be able to invalidate queries
(window as any).queryClient = queryClient;
(window as any).persistQueryClient = async () =>
  await persistQueryClientSave({
    queryClient,
    persister: reactQueryPersister,
  });

export function initializeQueryClient() {
  const axios = initializeAxios();
  QueryFactory.setBaseUrl(UniStorageFactory.UniStorage.BackendUri);
  QueryFactory.setAxiosFactory(() => axios);

  return queryClient;
}
export function getQueryClient() {
  return queryClient;
}
